import { Alert } from '@cmsgov/design-system';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button, ButtonVariation } from '../../../components/Button';
import { DismissibleAlert } from '../../../components/DismissibleAlert';
import { ChevronDirection, ChevronIcon, ExitIcon } from '../../../components/Icon';
import { PageLayout } from '../../../components/PageLayout';
import { RequiredFieldsLegend } from '../../../components/RequiredFieldsLegend';
import { StepItem, StepList, StepStatus, SubStepItem, SubStepStatus } from '../../../components/StepList';
import { useAlerts } from '../../../hooks';
import { useValidationAnnouncement } from '../../../hooks/useValidationAnnouncement';
import { ValidationAnnouncement } from '../../../components/ValidationAnnouncement';
import { ProgressTracker } from '../components/ProgressTracker';
import { SaveAndExitModal } from '../components/SaveAndExitModal';
import { PrimaryStep } from '../config/steps';
import { WORKFLOW_TITLE, workflowConfig } from '../config';
import { useApplication } from '../context';

import './ApplicationWorkflowLayout.css';

interface ApplicationWorkflowLayoutProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function ApplicationWorkflowLayout({ 'data-testid': dataTestId }: ApplicationWorkflowLayoutProps) {
  const baseTestId = dataTestId ?? 'application-workflow';
  const navigate = useNavigate();
  const {
    applicationId,
    currentStepIndex,
    currentSubStepIndex,
    currentStep,
    currentSubStep,
    formData,
    completedSubSteps,
    visitedSubSteps,
    highestStepReached,
    isFirstSubStep,
    isLastSubStepInCurrentStep,
    isReadOnly,
    goToNextSubStep,
    goToPreviousSubStep,
    goToStep,
    closeApplicationView,
    updateSubStepData,
    saveAndExit,
    saveCurrentProgress,
    showSuccessAlert,
    dismissSuccessAlert,
    fieldErrors,
    workflowAlert,
    dismissWorkflowAlert,
    isEditMode,
    saveAndReturnToReview,
    cancelEdit,
    openSignatureModal,
  } = useApplication();
  const { successAlert } = useAlerts();
  const isFinalReviewStep = currentStep?.id === PrimaryStep.FINAL_REVIEW;
  const isConfirmationStep = currentStep?.id === PrimaryStep.CONFIRMATION;
  const [isSaveExitModalOpen, setIsSaveExitModalOpen] = useState(false);
  const pageHeadingRef = useRef<HTMLHeadingElement>(null);
  const applicationStatusPath = applicationId
    ? `/application/status?applicationId=${encodeURIComponent(applicationId)}`
    : '/application/status';

  const subStepIndexLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    workflowConfig.steps.forEach((step) => {
      step.subSteps.forEach((subStep, subIndex) => {
        lookup.set(`${step.id}:${subStep.id}`, subIndex);
      });
    });

    return lookup;
  }, []);

  const visibleSubSteps = currentStep
    ? currentStep.subSteps.filter((substep) => !substep.shouldSkip || !substep.shouldSkip(formData))
    : [];

  const visibleSubStepIndices = currentStep
    ? currentStep.subSteps
        .map((substep, idx) => ({ originalIndex: idx, substep }))
        .filter(({ substep }) => !substep.shouldSkip || !substep.shouldSkip(formData))
        .map(({ originalIndex }) => originalIndex)
    : [];

  const currentVisibleIndex = currentStep ? visibleSubStepIndices.indexOf(currentSubStepIndex) : -1;

  const steps: StepItem[] = useMemo(
    () =>
      workflowConfig.steps.map((step, stepIndex) => {
        let status: StepStatus = StepStatus.UPCOMING;

        // Determine step status based on completion and navigation state
        // IMPORTANT: Check if it's the current step FIRST, before checking completion
        if (stepIndex === currentStepIndex) {
          status = StepStatus.CURRENT;
        } else {
          const allSubStepsCompleted = step.subSteps.every((substep) => {
            if (substep.shouldSkip && substep.shouldSkip(formData)) {
              return true;
            }

            const key = `${step.id}:${substep.id}`;

            return completedSubSteps.has(key);
          });

          if (allSubStepsCompleted) {
            status = StepStatus.COMPLETED;
          } else if (stepIndex < currentStepIndex || stepIndex <= highestStepReached) {
            status = StepStatus.IN_PROGRESS;
          }
          // else: status remains UPCOMING (default)
        }

        const visibleSubSteps = step.subSteps.filter((substep) => !substep.shouldSkip || !substep.shouldSkip(formData));

        const subSteps: SubStepItem[] = visibleSubSteps.map((substep) => {
          const key = `${step.id}:${substep.id}`;
          const isCurrent = stepIndex === currentStepIndex && substep.id === currentSubStep?.id;
          const isCompleted = completedSubSteps.has(key) || status === StepStatus.COMPLETED;
          const isAccessible = visitedSubSteps.has(key);

          let subStepStatus = SubStepStatus.UPCOMING;
          if (isCurrent) {
            subStepStatus = SubStepStatus.CURRENT;
          } else if (isCompleted) {
            subStepStatus = SubStepStatus.COMPLETED;
          } else if (isAccessible) {
            subStepStatus = SubStepStatus.ACCESSIBLE;
          }

          return {
            id: substep.id,
            status: subStepStatus,
            title: substep.title,
          };
        });

        const shouldShowSubSteps = subSteps.length > 1 || step.id === PrimaryStep.CITIZENSHIP_DETAILS;

        return {
          id: step.id,
          optional: step.optional,
          status,
          subSteps: shouldShowSubSteps ? subSteps : undefined,
          title: step.title,
        };
      }),
    [completedSubSteps, currentStepIndex, currentSubStep?.id, formData, highestStepReached, visitedSubSteps],
  );

  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!currentStep?.id) {
      return;
    }
    setExpandedStepIds((prev) => {
      const next = new Set(prev);
      next.add(currentStep.id);
      const previousStep = workflowConfig.steps[currentStepIndex - 1];
      if (previousStep) {
        const previousStepStatus = steps.find((step) => step.id === previousStep.id)?.status;
        if (previousStepStatus === StepStatus.COMPLETED) {
          next.delete(previousStep.id);
        }
      }

      return next;
    });
  }, [currentStep?.id, currentStepIndex, steps]);

  useEffect(() => {
    if (!currentStep?.id) {
      return;
    }

    window.scrollTo({ behavior: 'smooth', top: 0 });
    pageHeadingRef.current?.focus();
  }, [currentStep?.id, currentStepIndex, currentSubStepIndex]);

  const isLocalDev =
    import.meta.env.DEV &&
    import.meta.env.MODE === 'development' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const handleStepClick = (stepId: string) => {
    // Prevent navigation when in edit mode
    if (isEditMode) {
      return;
    }

    const stepIndex = workflowConfig.steps.findIndex((s) => s.id === stepId);
    const canNavigate = isLocalDev || stepIndex <= highestStepReached;

    if (stepIndex !== -1 && canNavigate) {
      goToStep(stepIndex, 0);
    }
  };

  const handleToggleStep = (stepId: string) => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }

      return next;
    });
  };

  const handleStepSubStepClick = (stepId: string, subStepId: string) => {
    if (isEditMode) {
      return;
    }

    const stepIndex = workflowConfig.steps.findIndex((s) => s.id === stepId);
    if (!isLocalDev && stepIndex > highestStepReached) {
      return;
    }
    const subStepIndex = subStepIndexLookup.get(`${stepId}:${subStepId}`);
    if (stepIndex !== -1 && subStepIndex !== undefined) {
      goToStep(stepIndex, subStepIndex);
    }
  };

  const handleSaveAndExitClick = () => {
    setIsSaveExitModalOpen(true);
  };

  const handleSaveExitModalClose = () => {
    setIsSaveExitModalOpen(false);
  };

  const handleSaveAndExitConfirm = () => {
    saveAndExit();
  };

  const handleNext = () => {
    // ACP-2077: arms the focus move below, so it only fires for a rejected attempt.
    // Batched with the errors goToNextSubStep sets, so the effect sees them.
    reportInvalidFields();
    goToNextSubStep();
  };

  const handleSaveAndReturnToReview = () => {
    // ACP-2092 / ACP-2077: Save stands in for Next on the last sub-step of a step being
    // edited from Final Review, and rejects an incomplete form the same way, so it has to
    // arm the same announcement and focus move. Without this it marked the field invalid
    // and said nothing, leaving focus on Save.
    reportInvalidFields();
    saveAndReturnToReview();
  };

  const handlePrevious = () => {
    goToPreviousSubStep();
  };

  const handleSaveCurrentProgress = async () => {
    if (!(await saveCurrentProgress())) return;
    successAlert({
      message: 'You are currently in your application. Continue completing the form below.',
      title: 'Your application has been saved.',
    });
  };

  const handleViewApplicationStatus = () => {
    navigate(applicationStatusPath);
  };

  // ACP-2077 / ACP-2092 (Section 508): a rejected advance has to be perceivable without
  // sight. Each field carries its own message as screen-reader text tied to the control
  // via aria-describedby (only RadioGroup also gives it role="alert"), so announcing the
  // invalid fields and moving focus to the first is what tells a keyboard or screen
  // reader user that the button did something and where to correct it, instead of
  // leaving them on a control that appeared inert.
  //
  // Unscoped on purpose: the validated fields make up the whole sub-step, which a child
  // component renders, so there is no single element here to hang a scope on.
  const fieldErrorSignature = Object.entries(fieldErrors)
    .filter(([, message]) => Boolean(message))
    .map(([field]) => field)
    .join('|');

  const { announcement: errorAnnouncement, reportInvalidFields } = useValidationAnnouncement({
    errorSignature: fieldErrorSignature,
  });

  if (!currentStep || !currentSubStep) {
    return (
      <div className='ds-u-padding--4'>
        <p>Loading workflow...</p>
      </div>
    );
  }

  const SubStepComponent = currentSubStep.component;
  const substepData = formData[currentStep.id]?.[currentSubStep.id] || currentSubStep.defaultData || {};

  // visibleSubSteps/currentVisibleIndex computed above for use in step mapping and progress tracker

  const completedVisibleSubStepIndices = new Set<number>();
  visibleSubSteps.forEach((substep, visibleIndex) => {
    const key = `${currentStep.id}:${substep.id}`;
    if (completedSubSteps.has(key)) {
      completedVisibleSubStepIndices.add(visibleIndex);
    }
  });
  const visitedVisibleSubStepIndices = new Set<number>();
  visibleSubSteps.forEach((substep, visibleIndex) => {
    const key = `${currentStep.id}:${substep.id}`;
    if (visitedSubSteps.has(key)) {
      visitedVisibleSubStepIndices.add(visibleIndex);
    }
  });

  // Calculate total visible substeps and current overall index
  // These are calculated but not currently used in the UI
  // Keeping for potential future progress indicators
  // const totalVisibleSubSteps = workflowConfig.steps.reduce((total, step) => {
  //   const stepVisibleSubSteps = step.subSteps.filter((substep) => !substep.shouldSkip || !substep.shouldSkip(formData));
  //   return total + stepVisibleSubSteps.length;
  // }, 0);

  // const currentOverallSubStepIndex = (() => {
  //   let count = 0;
  //   for (let i = 0; i < workflowConfig.steps.length; i++) {
  //     const step = workflowConfig.steps[i];
  //     const stepVisibleSubSteps = step.subSteps.filter(
  //       (substep) => !substep.shouldSkip || !substep.shouldSkip(formData),
  //     );
  //     if (i < currentStepIndex) {
  //       count += stepVisibleSubSteps.length;
  //     } else if (i === currentStepIndex) {
  //       count += currentVisibleIndex + 1;
  //       break;
  //     }
  //   }
  //   return count;
  // })();

  const handleSubStepClick = (visibleIndex: number) => {
    // Prevent navigation when in edit mode
    if (isEditMode) {
      return;
    }

    const stepIndexReachable = isLocalDev || currentStepIndex <= highestStepReached;
    if (
      !isLocalDev &&
      visibleIndex > currentVisibleIndex &&
      !completedVisibleSubStepIndices.has(visibleIndex) &&
      !visitedVisibleSubStepIndices.has(visibleIndex)
    ) {
      return;
    }

    if (!stepIndexReachable) {
      return;
    }

    const originalIndex = visibleSubStepIndices[visibleIndex];
    if (originalIndex !== undefined) {
      goToStep(currentStepIndex, originalIndex);
    }
  };

  const handleContinueFromAlert = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    dismissSuccessAlert();
  };

  const alertContent = workflowAlert ? (
    <DismissibleAlert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-alert`}
      heading={workflowAlert.heading}
      variation={workflowAlert.variation}
      onDismiss={dismissWorkflowAlert}>
      <p className='ds-u-margin-bottom--0'>{workflowAlert.text}</p>
    </DismissibleAlert>
  ) : isConfirmationStep ? (
    <Alert
      className='ds-u-margin-bottom--3'
      data-testid={`${baseTestId}-confirmation-alert`}
      heading='You have submitted your application.'
      role='status'
      variation='success'>
      <p className='ds-u-margin-bottom--0'>
        You may see the status of your application and learn more about each step in the process by going to the{' '}
        <Link
          className='ds-c-link'
          to={applicationStatusPath}>
          Application Status
        </Link>{' '}
        page.
      </p>
    </Alert>
  ) : (
    showSuccessAlert && (
      <Alert
        className='ds-u-margin-bottom--3'
        heading='Your application has been saved.'
        role='status'
        variation='success'>
        <p className='ds-u-margin-bottom--0'>
          You are currently in your application. Continue completing the form below.{' '}
          <a
            aria-label='Dismiss success message'
            className='ds-c-link'
            href='#dismiss'
            onClick={handleContinueFromAlert}>
            Dismiss this message
          </a>
          .
        </p>
      </Alert>
    )
  );

  const asideContent = (
    <div data-testid={`${baseTestId}-sidebar`}>
      <StepList
        allStepsClickable={isLocalDev && !isEditMode}
        data-testid={`${baseTestId}-steps`}
        expandedStepIds={expandedStepIds}
        showNumbers={true}
        steps={steps}
        onStepClick={handleStepClick}
        onSubStepClick={handleStepSubStepClick}
        onToggleStep={handleToggleStep}
      />

      <div
        className='ds-u-margin-top--3 ds-u-text-align--center'
        data-testid={`${baseTestId}-sidebar-actions`}>
        <Button
          isAutoWidth
          aria-label='Save and Exit Application - return to application home'
          data-testid={`${baseTestId}-save-exit`}
          variation={ButtonVariation.SOLID}
          onClick={handleSaveAndExitClick}>
          Save and Exit
          <span aria-hidden='true'>
            <ExitIcon />
          </span>
        </Button>
      </div>
    </div>
  );

  const mainContent = (
    <div data-testid={`${baseTestId}-content`}>
      <ValidationAnnouncement
        data-testid={`${baseTestId}-error-announcement`}
        message={errorAnnouncement}
      />
      {!isConfirmationStep && (
        <ProgressTracker
          allSubStepsClickable={isLocalDev && !isEditMode}
          completedSubStepIndices={completedVisibleSubStepIndices}
          currentSubStepIndex={currentVisibleIndex}
          data-testid={`${baseTestId}-progress`}
          totalSubSteps={visibleSubSteps.length}
          visitedSubStepIndices={visitedVisibleSubStepIndices}
          onSubStepClick={handleSubStepClick}
        />
      )}

      {!isConfirmationStep && (
        <div
          className='ds-u-margin-top--3 ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-header`}>
          <h2
            ref={pageHeadingRef}
            className='sub-step-heading ds-u-margin--0'
            data-testid={`${baseTestId}-title`}
            id='page-heading'
            tabIndex={-1}>
            {currentStep.title}
            <span
              className='sr-only'
              id='page-heading-substep'>
              {currentSubStep.title}
            </span>
          </h2>
          <RequiredFieldsLegend
            data-testid={`${baseTestId}-required-fields`}
            requiredFieldsText={currentSubStep.requiredFieldsText}
          />
        </div>
      )}

      <div data-testid={`${baseTestId}-form`}>
        <SubStepComponent
          allFormData={formData}
          data={substepData}
          fieldErrors={fieldErrors}
          updateData={updateSubStepData}
        />
      </div>

      <div
        className='button-progress-container'
        data-testid={`${baseTestId}-navigation`}>
        <div
          className='button-progress-buttons'
          data-testid={`${baseTestId}-buttons`}>
          {isConfirmationStep ? (
            <Button
              isAutoWidth
              aria-label='View Application Status - go to application status page'
              data-testid={`${baseTestId}-view-status`}
              variation={ButtonVariation.SOLID}
              onClick={handleViewApplicationStatus}>
              View Application Status
              <span aria-hidden='true'>
                <ChevronIcon
                  direction={ChevronDirection.RIGHT}
                  size={16}
                />
              </span>
            </Button>
          ) : isReadOnly ? (
            <Button
              isAutoWidth
              aria-label='Back - return to application home'
              data-testid={`${baseTestId}-back`}
              variation={ButtonVariation.SOLID}
              onClick={closeApplicationView}>
              <span aria-hidden='true'>
                <ChevronIcon
                  direction={ChevronDirection.LEFT}
                  size={16}
                />
              </span>
              Back
            </Button>
          ) : isEditMode ? (
            <>
              <Button
                isAutoWidth
                aria-label='Cancel - return to review without saving'
                data-testid={`${baseTestId}-cancel`}
                variation={ButtonVariation.SOLID}
                onClick={cancelEdit}>
                Cancel
              </Button>

              {isLastSubStepInCurrentStep ? (
                <Button
                  isAutoWidth
                  aria-label='Save - save changes and return to review'
                  data-testid={`${baseTestId}-save`}
                  variation={ButtonVariation.SOLID}
                  onClick={handleSaveAndReturnToReview}>
                  Save
                  <span aria-hidden='true'>
                    <ChevronIcon
                      direction={ChevronDirection.RIGHT}
                      size={16}
                    />
                  </span>
                </Button>
              ) : (
                <Button
                  isAutoWidth
                  aria-label='Next - proceed to next sub-step'
                  data-testid={`${baseTestId}-next`}
                  variation={ButtonVariation.SOLID}
                  onClick={handleNext}>
                  Next
                  <span aria-hidden='true'>
                    <ChevronIcon
                      direction={ChevronDirection.RIGHT}
                      size={16}
                    />
                  </span>
                </Button>
              )}
            </>
          ) : (
            <>
              {isFinalReviewStep ? (
                <Button
                  isAutoWidth
                  aria-label='Save - save your application'
                  data-testid={`${baseTestId}-save`}
                  variation={ButtonVariation.SOLID}
                  onClick={handleSaveCurrentProgress}>
                  Save
                </Button>
              ) : (
                !isFirstSubStep && (
                  <Button
                    isAutoWidth
                    aria-label='Previous - return to previous step'
                    data-testid={`${baseTestId}-previous`}
                    variation={ButtonVariation.SOLID}
                    onClick={handlePrevious}>
                    <span aria-hidden='true'>
                      <ChevronIcon
                        direction={ChevronDirection.LEFT}
                        size={16}
                      />
                    </span>
                    Previous
                  </Button>
                )
              )}

              {isFinalReviewStep ? (
                <Button
                  isAutoWidth
                  aria-label='Submit - sign and submit your application'
                  data-testid={`${baseTestId}-submit`}
                  variation={ButtonVariation.SOLID}
                  onClick={openSignatureModal}>
                  Submit
                </Button>
              ) : (
                <Button
                  isAutoWidth
                  aria-label='Next - proceed to next step'
                  data-testid={`${baseTestId}-next`}
                  variation={ButtonVariation.SOLID}
                  onClick={handleNext}>
                  Next
                  <span aria-hidden='true'>
                    <ChevronIcon
                      direction={ChevronDirection.RIGHT}
                      size={16}
                    />
                  </span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Show workflow title on all steps except confirmation
  const pageTitle = !isConfirmationStep ? WORKFLOW_TITLE : undefined;

  return (
    <PageLayout
      alert={alertContent}
      aside={isReadOnly || isConfirmationStep ? undefined : asideContent}
      asideAriaLabel={isReadOnly || isConfirmationStep ? undefined : 'Application navigation'}
      data-testid={baseTestId}
      mainAriaLabelledby='page-heading'
      mainId='content'
      mainTabIndex={-1}
      pageTitle={pageTitle}>
      {mainContent}
      {!isReadOnly && (
        <SaveAndExitModal
          data-testid={`${baseTestId}-save-exit-modal`}
          isOpen={isSaveExitModalOpen}
          onClose={handleSaveExitModalClose}
          onSaveAndExit={handleSaveAndExitConfirm}
        />
      )}
    </PageLayout>
  );
}

export default ApplicationWorkflowLayout;
